import React, { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { useAwin } from '../../hooks/use-awin'
import { useAddNonCinchOwnership } from '../../hooks/use-vehicle-ownerships'
import { getCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'
import { VehicleOwnershipSource } from '../../services/vehicle-ownership/types'
import {
  ConfirmationHero,
  ConfirmationNextSteps,
  KeyDatesPromo,
} from '@in-life-and-loyalty/vaps-ui'
import styles from './booking-confirmation.module.css'

export const BookingConfirmation: React.FC = () => {
  const context = useMaintenanceContext()
  const {
    customerEmail,
    customerSurname,
    vehicleMake,
    vehicleModel,
    vrm,
    store,
    notes,
    step,
    serviceOptions,
    serviceOptionsSelectedNames,
    serviceOptionsSelectedTotalPrice,
    appointmentType,
    bookingDate,
    bookingTime,
    canBookAppointment,
    vehicleVin,
    isNonCag,
  } = context

  const { sendAwinConfirmation } = useAwin()

  const vehicleOwnershipRequest =
    vehicleVin && vrm && vehicleMake && vehicleModel && customerEmail
      ? {
          source: VehicleOwnershipSource.WEB_SERVICE,
          vin: vehicleVin,
          vrm,
          make: vehicleMake,
          model: vehicleModel,
          emailAddress: customerEmail,
        }
      : undefined

  useAddNonCinchOwnership(vehicleOwnershipRequest)

  const bookingValueInPounds = Object.values(serviceOptions ?? {})
    .reduce((agg, cur) => (cur.selected ? agg + cur.price : agg), 0)
    .toFixed(2)

  useEffect(() => {
    datadogRum.addAction('Cinch Servicing: Booking Completed', {
      cinchServicing: true,
      email: customerEmail,
      step,
      page: window.location.pathname,
      serviceOptionsSelectedNames,
      serviceOptionsSelectedTotalPrice,
      bookingValueInPounds: Number.parseFloat(bookingValueInPounds),
      appointmentType,
      store,
      vehicleMake,
      vehicleModel,
      vehicleVin,
      vrm,
      notes,
      bookingDate,
      bookingTime,
      canBookAppointment,
      isNonCag,
    })

    const { data } = getCustomerJourneyCookie()

    if (data && data.commissionGroup === 'SERVICING') {
      const bookingRef = `${vrm}:${customerSurname}`
      sendAwinConfirmation(bookingRef, bookingValueInPounds, data, vehicleVin)
    }
  }, [])

  const isDirectBooking = canBookAppointment === true
  const serviceOptionText =
    serviceOptionsSelectedNames?.startsWith('Oil') ||
    serviceOptionsSelectedNames?.startsWith('MOT')
      ? `an ${serviceOptionsSelectedNames}`
      : `a ${serviceOptionsSelectedNames}`

  const title = isDirectBooking
    ? "We've got your booking!"
    : 'Thanks for your enquiry'
  const description = isDirectBooking
    ? `Your ${vehicleMake} ${vehicleModel}, ${vrm}, is booked for ${serviceOptionText} at ${store} | cinchServicing`
    : `Sit tight! Someone from the ${
        isNonCag ? `${store}` : 'Marshall'
      } team will call you to confirm your appointment soon.`
  const subHeading = isDirectBooking
    ? undefined
    : 'You should hear back within 48 hours, between Monday and Friday (8am-6pm).'

  return (
    <section className={styles.wrapper} data-testid="booking-confirmation">
      <main className={styles.content}>
        <ConfirmationHero
          isDirectBooking={isDirectBooking}
          title={title}
          description={description}
          subHeading={subHeading}
        />
        <KeyDatesPromo eventSource="cinch_servicing" />
        {isDirectBooking && <ConfirmationNextSteps />}
      </main>
    </section>
  )
}
